import { useState, useEffect } from "react";
import { Search, MapPin, SlidersHorizontal, ChevronUp } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { VET_SERVICE_CATEGORIES, VET_SERVICE_DESCRIPTIONS, GETVIDEOVET_SERVICE_DESCRIPTIONS } from "@/constants/vetServices";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ServiceSelectItem } from "@/components/ServiceSelectItem";
import { PET_TYPES } from "@/constants/petTypes";
import { SPECIALIST_BADGES } from "@/constants/badges";
import { cn } from "@/lib/utils";
import HeroBackdrop from "@/components/HeroBackdrop";
import { HERO } from "@/constants/hero";
import { trackEvent, EVENTS } from "@/lib/analytics";
import ValidatedPostcodeInput from "@/components/ValidatedPostcodeInput";

const petTypes = [...PET_TYPES].sort((a, b) => a.localeCompare(b));
const SLIDER_MAX = 500;

interface HeroSearchProps {
  /** When provided, the search submits inline instead of navigating to the landing page. */
  onSearch?: (params: URLSearchParams) => void;
}

const HeroSearch = ({ onSearch }: HeroSearchProps = {}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [petType, setPetType] = useState(() => searchParams.get("pet") ?? "");
  const [service, setService] = useState(() => searchParams.get("service") ?? "");
  const [postcode, setPostcode] = useState(() => searchParams.get("postcode") ?? "");

  // Keep inputs in sync if the URL params change (e.g. category-link navigation).
  useEffect(() => {
    const next = searchParams.get("postcode") ?? "";
    setPostcode((cur) => (cur === next ? cur : next));
  }, [searchParams]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedSpecialisms, setSelectedSpecialisms] = useState<string[]>([]);
  const [minBudget, setMinBudget] = useState(0);
  const [maxBudget, setMaxBudget] = useState(SLIDER_MAX);
  const [openNowOnly, setOpenNowOnly] = useState(false);
  const [onlineNowOnly, setOnlineNowOnly] = useState(false);
  const [backdropResetSignal, setBackdropResetSignal] = useState(0);
  const [backdropPaused, setBackdropPaused] = useState(false);
  const budgetActive = minBudget > 0 || maxBudget < SLIDER_MAX;

  const handleSearch = () => {
    // Reset the hero backdrop carousel to its first slide on every submit.
    setBackdropResetSignal((n) => n + 1);

    trackEvent(EVENTS.SEARCH_SUBMIT, {
      pet_type: petType || undefined,
      service: service || undefined,
      has_postcode: !!postcode.trim(),
      specialisms_count: selectedSpecialisms.length,
      open_now: openNowOnly,
      online_now: onlineNowOnly,
      budget_active: budgetActive,
    });

    const params = new URLSearchParams();
    if (petType) params.set("pet", petType);
    if (service) params.set("service", service);
    if (postcode.trim()) params.set("postcode", postcode.trim());
    if (selectedSpecialisms.length > 0) params.set("specialisms", selectedSpecialisms.join(","));
    if (budgetActive) {
      params.set("minBudget", String(minBudget));
      params.set("maxBudget", String(maxBudget));
    }
    if (openNowOnly) params.set("openNow", "1");
    if (onlineNowOnly) params.set("onlineNow", "1");
    if (onSearch) {
      onSearch(params);
      return;
    }
    const qs = params.toString();
    navigate(`/${qs ? `?${qs}` : ""}`);
  };

  return (
    <section
      className="relative overflow-hidden"
      aria-label="Find a vet"
      onMouseEnter={() => setBackdropPaused(true)}
      onMouseLeave={() => setBackdropPaused(false)}
      onFocusCapture={() => setBackdropPaused(true)}
      onBlurCapture={(e) => {
        // Only resume when focus leaves the entire section.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setBackdropPaused(false);
        }
      }}
      onTouchStart={() => setBackdropPaused(true)}
      onTouchEnd={() => setBackdropPaused(false)}
    >
      <HeroBackdrop resetSignal={backdropResetSignal} paused={backdropPaused} />

      <div className="relative max-w-4xl mx-auto px-4 pt-8 pb-8 md:pt-12 md:pb-10">
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center gap-2 text-foreground text-sm font-medium mb-6 dark:bg-background/80 dark:border dark:border-primary/30 dark:px-5 dark:py-2.5 dark:rounded-full dark:backdrop-blur-md dark:shadow-sm">
            <MapPin className="w-4 h-4 text-primary" />
            {HERO.trustChip}
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold text-foreground tracking-tight mb-4">
            {HERO.headline}
          </h1>

          <p className="text-foreground/90 max-w-xl mb-8 leading-relaxed">
            {HERO.subtitle}
          </p>

          <div className="w-full bg-background/95 backdrop-blur-md border border-border rounded-xl p-6 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block text-left">Pet Type</label>
                <Select value={petType} onValueChange={setPetType}>
                  <SelectTrigger aria-label="Pet type">
                    <SelectValue placeholder="Select pet type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {petTypes.map((type) => (
                      <SelectItem key={type} value={type.toLowerCase()}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block text-left">Service Needed</label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger aria-label="Service needed">
                    <SelectValue placeholder="Select service..." />
                  </SelectTrigger>
                  <SelectContent>
                    <TooltipProvider delayDuration={0} disableHoverableContent>
                      {VET_SERVICE_CATEGORIES.map((cat) => (
                        <SelectGroup key={cat.label}>
                          <SelectLabel className="text-primary">{cat.label}</SelectLabel>
                          {cat.services.map((s) => (
                            <ServiceSelectItem
                              key={`${cat.label}-${s}`}
                              value={s.toLowerCase()}
                              description={(VET_SERVICE_DESCRIPTIONS as Record<string, string>)[s] ?? (GETVIDEOVET_SERVICE_DESCRIPTIONS as Record<string, string>)[s]}
                            >
                              {s}
                            </ServiceSelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </TooltipProvider>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block text-left">UK Postcode</label>
                <ValidatedPostcodeInput
                  className="bg-muted/50"
                  value={postcode}
                  onChange={setPostcode}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  showUseMyLocation
                />
              </div>
            </div>

            {advancedOpen && (
              <div className="mb-4 space-y-4 border-t border-border pt-4 text-left">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-foreground">Specialisms</label>
                    {selectedSpecialisms.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedSpecialisms([])}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear ({selectedSpecialisms.length})
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {SPECIALIST_BADGES.map((badge) => {
                      const Icon = badge.icon;
                      const active = selectedSpecialisms.includes(badge.id);
                      return (
                        <button
                          key={badge.id}
                          type="button"
                          onClick={() => setSelectedSpecialisms((prev) => (
                            prev.includes(badge.id)
                              ? prev.filter((item) => item !== badge.id)
                              : [...prev, badge.id]
                          ))}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-foreground border-border hover:border-primary hover:bg-primary/5",
                          )}
                          aria-pressed={active}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {badge.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="flex items-baseline justify-between mb-2">
                    <label className="text-sm font-medium text-foreground">
                      Budget {service && <span className="text-xs text-muted-foreground font-normal">- for {service}</span>}
                    </label>
                    <span className="text-sm font-semibold text-foreground tabular-nums">
                      {!service
                        ? "Select a service"
                        : `£${minBudget} - ${maxBudget >= SLIDER_MAX ? `£${SLIDER_MAX}+` : `£${maxBudget}`}`}
                    </span>
                  </div>
                  <Slider
                    value={[minBudget, maxBudget]}
                    min={0}
                    max={SLIDER_MAX}
                    step={5}
                    onValueChange={(v) => { setMinBudget(v[0]); setMaxBudget(v[1]); }}
                    disabled={!service}
                    aria-label="Budget range"
                    className={cn(!service && "opacity-50")}
                  />
                  <div className="flex items-center justify-between mt-1.5 text-[11px] text-muted-foreground">
                    <span>£0</span>
                    <span>£{SLIDER_MAX}+</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Switch id="hero-open-now-toggle" checked={openNowOnly} onCheckedChange={setOpenNowOnly} />
                    <Label htmlFor="hero-open-now-toggle" className="text-sm cursor-pointer">Open Now</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="hero-online-now-toggle" checked={onlineNowOnly} onCheckedChange={setOnlineNowOnly} />
                    <Label htmlFor="hero-online-now-toggle" className="text-sm cursor-pointer flex items-center gap-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
                      Online now
                    </Label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button className="flex-1 gap-2 py-5 text-base" onClick={handleSearch}>
                <Search className="w-4 h-4" />
                {HERO.ctaLabel}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="gap-2 sm:w-auto"
                onClick={() => setAdvancedOpen((v) => !v)}
                aria-expanded={advancedOpen}
              >
                {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
                {advancedOpen ? "Hide advanced" : "Advanced search"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
